import type { InvoiceResponse, InvoiceListStatus } from '@/shared/api';

export function invoiceStatus(invoice: InvoiceResponse): InvoiceListStatus {
  if (invoice.status) return invoice.status;
  if (invoice.invoiceNumber === null) return 'Draft';
  if (invoice.correctionKind === 'FullReversal') return 'CreditNote';
  if (invoice.correctionKind === 'PartialAdjustment') return 'Correction';
  if (invoice.isCancelled) return 'Cancelled';
  return invoice.paidAt ? 'Paid' : 'Open';
}

export type InvoiceStepState = 'none' | 'open' | 'paid';

/** Stand der Rechnung für den Schritt im Kopf: keine aktive, mindestens eine offen, alle bezahlt. */
export function invoiceStepState(invoices: InvoiceResponse[]): InvoiceStepState {
  const active = invoices.map(invoiceStatus).filter((s) => s === 'Open' || s === 'Paid');
  if (active.length === 0) return 'none';
  return active.includes('Open') ? 'open' : 'paid';
}

export function invoiceHistory(invoice: InvoiceResponse): InvoiceResponse[] {
  return [invoice, ...(invoice.corrections ?? [])].sort((left, right) => {
    if (left.id === invoice.id && right.id !== invoice.id) return -1;
    if (right.id === invoice.id && left.id !== invoice.id) return 1;
    return (left.correctionSequence ?? 0) - (right.correctionSequence ?? 0);
  });
}

export function currentInvoice(invoice: InvoiceResponse): InvoiceResponse {
  const replacement = invoiceHistory(invoice)
    .filter(
      (document) =>
        document.correctionKind === 'AmountReplacement' ||
        document.correctionKind === 'RecipientReplacement',
    )
    .at(-1);
  return replacement ?? invoice;
}

function effectiveTotal(invoice: InvoiceResponse): number {
  const current = currentInvoice(invoice);
  const history = invoiceHistory(invoice);
  const currentIndex = history.findIndex((document) => document.id === current.id);
  return history
    .slice(currentIndex + 1)
    .reduce(
      (total, document) => total + (document.financialDeltaAmount ?? document.totalAmount),
      current.totalAmount,
    );
}

export function summarizeInvoiceGroups(invoices: InvoiceResponse[], bookingCurrency: string) {
  const currencyMismatch = invoices.some((invoice) => invoice.currency !== bookingCurrency);
  let activeTotal = 0;
  let paidTotal = 0;
  let openTotal = 0;
  for (const invoice of invoices) {
    const status = invoiceStatus(invoice);
    const total = effectiveTotal(invoice);
    if (status === 'Paid') {
      activeTotal += total;
      paidTotal += total;
    } else if (status === 'Open') {
      activeTotal += total;
      openTotal += total;
    }
  }
  return {
    groupCount: invoices.length,
    activeTotal: currencyMismatch ? 0 : activeTotal,
    paidTotal: currencyMismatch ? 0 : paidTotal,
    openTotal: currencyMismatch ? 0 : openTotal,
    currencyMismatch,
  };
}
